import { useMemo, useState } from 'react';
import type {
  HomeAnchorMappingApplyResultDTO,
  HomeAnchorMappingRowDTO,
  HomeAnchorMappingSkippedDTO,
  HomeAnchorStationCountDTO,
} from 'api/model';
import {
  HomeAnchorMappingKindEnum,
  HomeAnchorMappingSkipReasonEnum,
} from 'api/model';
import {
  useGlobalSettingsApplyHomeAnchorMapping,
  useGlobalSettingsPreviewHomeAnchorMapping,
} from 'api/global-settings/global-settings';
import styled from 'styled-components';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showException } from 'utils/exception-helper';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import Checkbox from 'components/inputs/Checkbox';
import TextButton from 'components/inputs/TextButton';
import ConfirmModal from 'components/Modal/ConfirmModal';

const Wrapper = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 1100px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const Summary = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  font-size: 13px;
`;

const Figure = styled.div`
  display: flex;
  flex-direction: column;
`;

const FigureValue = styled.span`
  font-size: 20px;
  font-weight: 600;
`;

const FigureLabel = styled.span`
  font-size: 11px;
  color: #666;
`;

const Muted = styled.span`
  color: #999;
  font-style: italic;
`;

const SubHeading = styled.h3`
  margin: 10px 0 0;
  font-size: 14px;
`;

const Note = styled.p`
  font-size: 12px;
  color: #666;
  margin: 0;
`;

/** Tomt postnummer och okänt prefix kräver olika åtgärd, så orsaken skrivs ut. */
const SKIP_REASON_TEXT: Record<HomeAnchorMappingSkipReasonEnum, string> = {
  [HomeAnchorMappingSkipReasonEnum.PostalCodeMissing]:
    'Postnummer saknas på personalkortet',
  [HomeAnchorMappingSkipReasonEnum.UnknownPostalCodePrefix]:
    'Ingen koordinatsatt adress i samma postnummerområde att mäta från',
};

/**
 * Vad som händer med listan. Record över HELA enumen, så att ett nytt fall från backend blir ett
 * typfel här i stället för en tom etikett.
 */
const KIND_TEXT: Record<HomeAnchorMappingKindEnum, string> = {
  [HomeAnchorMappingKindEnum.Missing]: 'saknade hemankare',
  [HomeAnchorMappingKindEnum.Added]: 'stationen läggs sist',
  [HomeAnchorMappingKindEnum.Change]: 'stationen byts ut',
  [HomeAnchorMappingKindEnum.Unchanged]: 'oförändrat',
};

const km = (value: number | null | undefined) =>
  value == null ? '' : `${Math.round(value)} km`;

const HomeAnchorMappingPage = () => {
  // Förhandsvisningen läser hela uppdragshistorikens koordinater för att bygga
  // postnummercentroiderna, så den hämtas bara när någon ber om den — inte vid sidladdning.
  const preview = useGlobalSettingsPreviewHomeAnchorMapping({
    query: { enabled: false },
  });
  const applyCall = useGlobalSettingsApplyHomeAnchorMapping();

  const [showUnchanged, setShowUnchanged] = useState(false);
  const [pendingMode, setPendingMode] = useState<'missing' | 'all' | null>(null);
  const [result, setResult] = useState<HomeAnchorMappingApplyResultDTO | null>(
    null
  );

  const { data } = preview;

  // Standardläget skriver luckorna OCH lägger till saknade stationer — inget tas bort. Det
  // uttryckliga läget byter dessutom en annan station i listan (flera-hemankare-plan.md § 4).
  const defaultCount = (data?.missingCount ?? 0) + (data?.addedCount ?? 0);
  const allCount = defaultCount + (data?.changeCount ?? 0);

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    return showUnchanged
      ? all
      : all.filter((r) => r.kind !== HomeAnchorMappingKindEnum.Unchanged);
  }, [data, showUnchanged]);

  const loadPreview = async () => {
    setResult(null);
    try {
      await preview.refetch();
    } catch (error) {
      showException(error);
    }
  };

  const apply = async (includeAlreadyAnchored: boolean) => {
    try {
      const applied = await applyCall.mutateAsync({
        data: { includeAlreadyAnchored },
      });
      setResult(applied);
      // Hämta om, så listan visar läget efter skrivningen i stället för förslaget före.
      await preview.refetch();
    } catch (error) {
      showException(error);
    } finally {
      setPendingMode(null);
    }
  };

  const columnSettings = useMemo(
    (): ColumnSetting<HomeAnchorMappingRowDTO, object>[] => [
      { head: 'Fälttestare', width: 200, render: (r) => r.userName },
      { head: 'Postnr', width: 80, render: (r) => r.postalCode ?? '' },
      {
        head: 'Nuvarande hemankare',
        width: 260,
        render: (r) =>
          r.currentAnchorNames.length > 0 ? (
            <>
              {r.currentAnchorNames.join(', ')}
              {/* Avståndet till den station som redan står i listan — jämförelsetalet. */}
              {r.currentDistanceKm != null
                ? ` (${km(r.currentDistanceKm)})`
                : ''}
            </>
          ) : (
            <Muted>saknas</Muted>
          ),
      },
      {
        head: 'Blir',
        width: 300,
        render: (r) =>
          r.kind === HomeAnchorMappingKindEnum.Unchanged ? (
            <Muted>oförändrat</Muted>
          ) : (
            <>
              <strong>{r.resultAnchorNames.join(', ')}</strong>
              <Muted>{` — ${KIND_TEXT[r.kind] ?? ''}`}</Muted>
            </>
          ),
      },
      { head: 'Fågelväg', width: 90, render: (r) => km(r.distanceKm) },
    ],
    []
  );

  const stationColumns = useMemo(
    (): ColumnSetting<HomeAnchorStationCountDTO, object>[] => [
      { head: 'Station', width: 260, render: (s) => s.anchorName },
      { head: 'Fälttestare', width: 110, render: (s) => s.count },
    ],
    []
  );

  const skippedColumns = useMemo(
    (): ColumnSetting<HomeAnchorMappingSkippedDTO, object>[] => [
      { head: 'Fälttestare', width: 200, render: (s) => s.userName },
      { head: 'Postnr', width: 80, render: (s) => s.postalCode ?? '' },
      {
        head: 'Nuvarande hemankare',
        width: 220,
        render: (s) => s.currentAnchorName ?? <Muted>saknas</Muted>,
      },
      {
        head: 'Orsak',
        width: 420,
        render: (s) => SKIP_REASON_TEXT[s.reason] ?? '',
      },
    ],
    []
  );

  const isBusy = preview.isFetching || applyCall.isLoading;

  return (
    <Wrapper>
      <h1>Hemankare — mappa mot närmaste centralstation</h1>
      <Note>
        Ser till att den närmaste företagsposten som är flaggad som{' '}
        <strong>centralstation</strong> och har koordinat finns bland varje
        aktiv fälttestares <strong>hemankare</strong>. Utan hemankare utesluts
        fälttestaren ur ruttplaneringens förslag, eftersom dagen räknas från
        ankaret och inte från bostadsadressen.
      </Note>
      <Note>
        En fälttestare kan ha flera hemankare, till exempel en föredragen hubb
        och en centralstation. Planeringen utgår från det som ligger närmast
        dagens första respektive sista uppdrag. Har någon redan ett ankare men
        ingen station läggs stationen <strong>sist</strong> i listan — övriga
        ankare rörs aldrig.
      </Note>
      <Note>
        Närmast mäts som fågelväg från en postnummercentroid byggd av
        uppdragshistorikens koordinatsatta adresser — inte som skillnad i
        postnummer, vilket skulle placera Järfälla (177&nbsp;xx) närmare Malmö C
        (211&nbsp;xx) än Stockholm C (111&nbsp;xx). Förhandsvisningen skriver
        ingenting; den räknar med exakt samma metod som knapparna använder.
      </Note>

      <Row>
        <TextButton onClick={loadPreview} disabled={isBusy}>
          {preview.isFetching ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : data ? (
            'Räkna om förhandsgranskningen'
          ) : (
            'Visa förhandsgranskning'
          )}
        </TextButton>
        {data ? (
          <Checkbox
            checked={showUnchanged}
            onChange={(e) => setShowUnchanged(e.currentTarget.checked)}
          >
            Visa även de som redan har närmaste stationen
          </Checkbox>
        ) : null}
      </Row>

      {data ? (
        <>
          <Summary>
            <Figure>
              <FigureValue>{data.missingCount}</FigureValue>
              <FigureLabel>saknar hemankare</FigureLabel>
            </Figure>
            <Figure>
              <FigureValue>{data.addedCount}</FigureValue>
              <FigureLabel>har ankare men ingen station</FigureLabel>
            </Figure>
            <Figure>
              <FigureValue>{data.changeCount}</FigureValue>
              <FigureLabel>har en annan station än den närmaste</FigureLabel>
            </Figure>
            <Figure>
              <FigureValue>{data.unchangedCount}</FigureValue>
              <FigureLabel>har redan närmaste stationen</FigureLabel>
            </Figure>
            <Figure>
              <FigureValue>{data.skipped.length}</FigureValue>
              <FigureLabel>kan inte mappas</FigureLabel>
            </Figure>
            <Figure>
              <FigureValue>{data.stationCount}</FigureValue>
              <FigureLabel>stationer med koordinat</FigureLabel>
            </Figure>
            <Figure>
              <FigureValue>{data.fieldTesterCount}</FigureValue>
              <FigureLabel>aktiva fälttestare totalt</FigureLabel>
            </Figure>
          </Summary>

          {data.stationCount === 0 ? (
            <Note>
              Inga företag är flaggade som centralstation med koordinat. Flagga
              stationerna under Företag och se till att de har en koordinat —
              annars finns ingenting att mappa mot.
            </Note>
          ) : null}

          {result ? (
            <Note>
              <strong>
                Sparade {result.updatedCount} hemankare
                {result.updatedCount > 0
                  ? ` (${result.missingUpdatedCount} nya, ${result.addedUpdatedCount} med station tillagd, ${result.changedUpdatedCount} bytta)`
                  : ''}
                .
              </strong>{' '}
              Varje ändring ligger i fälttestarens ändringslogg under
              &quot;Hemankare&quot;.
            </Note>
          ) : null}

          <Table fluid columnSettings={columnSettings} rows={rows} />

          <SubHeading>Fördelning per station</SubHeading>
          <Note>
            Den viktigaste kontrollen: fördelningen ska likna var verksamheten
            faktiskt arbetar. Sticker en station i södra Sverige ut mot
            Stockholm är avståndsmåttet fel, inte datat.
          </Note>
          <Table
            fluid
            columnSettings={stationColumns}
            rows={data.perStation}
          />

          {data.skipped.length > 0 ? (
            <>
              <SubHeading>
                Kan inte mappas ({data.skipped.length})
              </SubHeading>
              <Note>
                De här måste sättas för hand på personalkortet, annars står de
                utanför planeringen. En del är testkonton som i stället borde
                tappa rollen Fälttestare.
              </Note>
              <Table
                fluid
                columnSettings={skippedColumns}
                rows={data.skipped}
              />
            </>
          ) : null}

          <Row>
            <TextButton
              onClick={() => setPendingMode('missing')}
              disabled={isBusy || defaultCount === 0}
            >
              Lägg till station hos de {defaultCount} som saknar
            </TextButton>
            <TextButton
              onClick={() => setPendingMode('all')}
              disabled={isBusy || allCount === 0}
            >
              Mappa om alla {allCount}
            </TextButton>
            {applyCall.isLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : null}
          </Row>
          <Note>
            &quot;Mappa om alla&quot; byter dessutom en annan station i listan
            mot den närmaste, även om någon satt den för hand. Det är avsiktligt
            när ankarna ska följa en enhetlig regel, men ett medvetet undantag —
            en fälttestare som utgår från en annan station än den närmaste —
            försvinner då. Hubbar och andra ankare rörs inte. Ändringarna går att
            spåra per person i ändringsloggen.
          </Note>
        </>
      ) : null}

      {pendingMode ? (
        <ConfirmModal
          title={
            pendingMode === 'missing'
              ? 'Lägg till station hos de som saknar'
              : 'Mappa om alla fälttestare'
          }
          content={
            pendingMode === 'missing'
              ? `${defaultCount} fälttestare får närmaste centralstation: ${data?.missingCount ?? 0} som saknar hemankare, och ${data?.addedCount ?? 0} som har ankare men ingen station (stationen läggs sist). Inga satta ankare tas bort.`
              : `${allCount} fälttestare får närmaste centralstation i listan. Av dem har ${data?.changeCount ?? 0} redan en annan station, som byts ut.`
          }
          confirmText="Spara"
          disabled={applyCall.isLoading}
          onConfirm={() => apply(pendingMode === 'all')}
          onCancel={() => setPendingMode(null)}
        />
      ) : null}
    </Wrapper>
  );
};

export default HomeAnchorMappingPage;
